import {
  Box,
  TextField,
  Typography,
  IconButton,
  makeStyles,
  Tooltip,
  Button,
} from "@material-ui/core";
import React, { useState, useEffect, useContext, useMemo } from "react";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import MinimizeIcon from "@material-ui/icons/Minimize";
import { intl } from "../intl";
import { CallMadeOutlined } from "@material-ui/icons";
import {
  editMeeting,
  fetchMeeting,
  setOfficialReportOpen,
} from "../actions/Management/meetingManagementAction";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import GuardianDownloadIcon from "./Management/Messaging/icons/GuardianDownload";
import RoomContext from "../context/RoomContext";
import { setOfficialReportOpenGlobal } from "../actions/roomActions";
import { exportOfficialReportExcel } from "../helpers/exportOfficialReportExcel";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#fff",
    fontFamily: "'Inter', sans-serif",
    color: "#0f172a",
  },
  minimized: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#eef4ff",
    borderRadius: 10,
    height: 36,
    cursor: "pointer",
    overflow: "hidden",
    padding: "0 8px 0 4px",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
    gap: 8,
  },
  toolbarActions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  toolBtn: {
    borderRadius: 10,
    color: "#2540E4",
  },
  card: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
  },
  mainTitle: {
    textAlign: "center",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 0.2,
    background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
    borderBottom: "1px solid #e2e8f0",
    padding: "12px 16px",
    color: "#0f172a",
  },
  section: {
    borderBottom: "1px solid #e2e8f0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "10px 14px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#334155",
  },
  row: {
    display: "flex",
    alignItems: "stretch",
    borderBottom: "1px solid #f1f5f9",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  labelCell: {
    flex: "0 0 120px",
    maxWidth: 140,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 8px",
    background: "#f8fafc",
    borderRight: "1px solid #e2e8f0",
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    textAlign: "center",
    boxSizing: "border-box",
  },
  indexCell: {
    flex: "0 0 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 4px",
    background: "#f8fafc",
    borderRight: "1px solid #e2e8f0",
    fontSize: 12,
    fontWeight: 600,
    color: "#94a3b8",
    boxSizing: "border-box",
  },
  fieldCell: {
    flex: 1,
    minWidth: 0,
    padding: "4px 8px",
    boxSizing: "border-box",
  },
  colHead: {
    flex: 1,
    minWidth: 0,
    padding: "8px 10px",
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    background: "#f8fafc",
    borderRight: "1px solid #e2e8f0",
    boxSizing: "border-box",
    "&:last-child": {
      borderRight: "none",
    },
  },
  textField: {
    width: "100%",
    marginTop: 0,
    "& .MuiInputBase-root": {
      fontSize: 13,
      lineHeight: 1.4,
    },
    "& .MuiInputBase-input": {
      padding: "8px 4px",
    },
  },
  addRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    background: "#fafbfc",
  },
  addBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12,
    color: "#2540E4",
    borderColor: "#c7d2fe",
    background: "#eef2ff",
    padding: "4px 12px",
    "&:hover": {
      background: "#e0e7ff",
      borderColor: "#a5b4fc",
    },
  },
  deleteCell: {
    flex: "0 0 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 2px",
    boxSizing: "border-box",
    borderLeft: "1px solid #f1f5f9",
  },
  deleteBtn: {
    color: "#94a3b8",
    padding: 4,
    "&:hover": {
      color: "#dc2626",
      background: "#fef2f2",
    },
  },
  emptyHint: {
    padding: "12px 14px",
    fontSize: 13,
    color: "#94a3b8",
  },
}));

const emptyParticipant = () => ({
  side: "",
  position: "",
  displayName: "",
  email: "",
  phoneNumber: "",
});

const emptyTodo = () => ({
  todo: "",
  owner: "",
  endDate: "",
});

const personName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

const hasMeaningfulParticipants = (list) =>
  Array.isArray(list) &&
  list.some(
    (p) =>
      p &&
      (String(p.displayName || "").trim() ||
        String(p.email || "").trim() ||
        String(p.phoneNumber || "").trim() ||
        String(p.side || "").trim() ||
        String(p.position || "").trim())
  );

const buildParticipantsFromMeeting = (meeting, room, peers) => {
  const source = meeting || room || {};
  const users = source.Users || source.users || [];
  const external = source.externalUserEmails || [];
  const guests = source.guestUserNames || [];
  const creator = source.creator || room?.creator;
  const list = [];
  const seen = new Set();

  const push = (entry) => {
    const key = `${String(entry.email || "")
      .trim()
      .toLowerCase()}|${String(entry.displayName || "")
      .trim()
      .toLowerCase()}`;
    if (key === "|" || seen.has(key)) return;
    seen.add(key);
    list.push(entry);
  };

  if (creator) {
    push({
      side: "İç",
      position: creator.position || "",
      displayName: personName(creator),
      email: creator.email || "",
      phoneNumber: creator.phoneNumber || "",
    });
  }

  users.forEach((u) => {
    push({
      side: "İç",
      position: u.position || "",
      displayName: personName(u) || u.displayName || "",
      email: u.email || "",
      phoneNumber: u.phoneNumber || "",
    });
  });

  external.forEach((item) => {
    const email = typeof item === "string" ? item : item?.email || "";
    push({
      side: "Dış",
      position: "",
      displayName: typeof item === "object" ? personName(item) : "",
      email,
      phoneNumber: "",
    });
  });

  guests.forEach((item) => {
    const name = typeof item === "string" ? item : item?.name || item?.displayName || "";
    push({
      side: "Misafir",
      position: "",
      displayName: name,
      email: typeof item === "object" ? item?.email || "" : "",
      phoneNumber: "",
    });
  });

  if (peers && typeof peers === "object") {
    Object.values(peers).forEach((peer) => {
      if (!peer) return;
      push({
        side: "Katılımcı",
        position: "",
        displayName: peer.displayName || "",
        email: peer.email || "",
        phoneNumber: "",
      });
    });
  }

  return list.length ? list : [emptyParticipant()];
};

const ensureMin = (arr, min, factory) => {
  const next = Array.isArray(arr) ? [...arr] : [];
  while (next.length < min) next.push(factory());
  return next;
};

const ConferenceOfficialReport = ({
  room,
  edit = true,
  isDownload,
  onDownloadButtonClick,
  isDownloading = false,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const roomContext = useContext(RoomContext);
  const roomClient = roomContext?.roomClient;
  const me = useSelector((state) => state.user.me);
  const guest = useSelector((state) => state.me);
  const peers = useSelector((state) => state.peers);
  const selectedMeeting = useSelector(
    (state) => state.meetingManagement.selectedMeeting
  );
  const isOpenOfficialReport = useSelector(
    (state) => state.meetingManagement.isOpenOfficialReport
  );

  let canEdit = edit;
  if (guest && guest.id?.length === 8) canEdit = false;
  if (me?.id !== selectedMeeting?.creator?.id) canEdit = false;

  let roomId;
  if (room?.id) roomId = room.id;
  else {
    const arr = window.location.href.split("/");
    roomId = arr[arr.length - 1];
  }

  const report = selectedMeeting?.officialReport;

  const [subject, setSubject] = useState(report?.subject || room?.name || "");
  const [topics, setTopics] = useState(ensureMin(report?.topics, 3, () => ""));
  const [todos, setTodos] = useState(ensureMin(report?.todos, 3, emptyTodo));
  const [desicions, setDesicions] = useState(
    ensureMin(report?.desicions, 3, () => "")
  );
  const [participants, setParticipants] = useState(() =>
    hasMeaningfulParticipants(report?.participants)
      ? report.participants
      : buildParticipantsFromMeeting(selectedMeeting, room, peers)
  );

  const isCreator = me?.id && me.id === selectedMeeting?.creator?.id;
  const readOnly = !canEdit || !isCreator;

  const dateValue = useMemo(() => {
    const raw = room?.startTime || room?.startDate;
    if (!raw) return "";
    try {
      return new Date(raw).toLocaleString();
    } catch (e) {
      return String(raw);
    }
  }, [room?.startTime, room?.startDate]);

  useEffect(() => {
    if (!selectedMeeting) return;

    setSubject(
      selectedMeeting?.officialReport?.subject ||
        selectedMeeting?.name ||
        room?.name ||
        ""
    );
    setTopics(ensureMin(selectedMeeting?.officialReport?.topics, 3, () => ""));
    setTodos(ensureMin(selectedMeeting?.officialReport?.todos, 3, emptyTodo));
    setDesicions(
      ensureMin(selectedMeeting?.officialReport?.desicions, 3, () => "")
    );

    const saved = selectedMeeting?.officialReport?.participants;
    if (hasMeaningfulParticipants(saved)) {
      setParticipants(saved);
    } else {
      setParticipants(
        buildParticipantsFromMeeting(selectedMeeting, room, peers)
      );
    }
    // peers yalnızca boş tutanakta ilk doldurma için; canlı peer değişikliklerinde formu sıfırlama
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeeting]);

  useEffect(() => {
    if (me?.id?.length > 8) getMeeting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpenOfficialReport === true && me?.id?.length > 8) getMeeting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenOfficialReport]);

  const getMeeting = () => {
    if (roomId) dispatch(fetchMeeting(roomId));
  };

  const updateTopic = (index, value) => {
    const next = [...topics];
    next[index] = value;
    setTopics(next);
  };

  const updateTodo = (index, field, value) => {
    const next = [...todos];
    next[index] = {
      todo: next[index]?.todo || "",
      owner: next[index]?.owner || "",
      endDate: next[index]?.endDate || "",
      [field]: value,
    };
    setTodos(next);
  };

  const updateDecision = (index, value) => {
    const next = [...desicions];
    next[index] = value;
    setDesicions(next);
  };

  const updateParticipant = (index, field, value) => {
    const next = [...participants];
    next[index] = { ...emptyParticipant(), ...next[index], [field]: value };
    setParticipants(next);
  };

  const handleAddTopic = () => setTopics([...topics, ""]);
  const handleAddTodo = () => setTodos([...todos, emptyTodo()]);
  const handleAddDesicion = () => setDesicions([...desicions, ""]);
  const handleAddParticipants = () =>
    setParticipants([...participants, emptyParticipant()]);

  const removeAt = (list, index) => list.filter((_, i) => i !== index);
  const handleRemoveTopic = (index) => setTopics(removeAt(topics, index));
  const handleRemoveTodo = (index) => setTodos(removeAt(todos, index));
  const handleRemoveDesicion = (index) =>
    setDesicions(removeAt(desicions, index));
  const handleRemoveParticipant = (index) =>
    setParticipants(removeAt(participants, index));

  const DeleteRowButton = ({ onClick, label }) => (
    <div className={classes.deleteCell}>
      <Tooltip title={label || "Sil"}>
        <IconButton
          className={classes.deleteBtn}
          size="small"
          onClick={onClick}
          aria-label={label || "Sil"}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const handleSaveOfficialReport = () => {
    if (me?.id !== selectedMeeting?.creator?.id) return;
    const body = {
      id: roomId,
      officialReport: {
        dateTime: room?.startTime || room?.startDate,
        subject,
        topics,
        todos,
        desicions,
        participants,
      },
    };
    dispatch(editMeeting(body));
    if (roomClient?.sendMeetingDetails) {
      roomClient.sendMeetingDetails(body);
    }
  };

  const handleDownloadExcel = () => {
    const rawDate = room?.startTime || room?.startDate;
    let dateLabel = "";
    try {
      dateLabel = rawDate ? new Date(rawDate).toLocaleString() : "";
    } catch (e) {
      dateLabel = rawDate == null ? "" : String(rawDate);
    }
    const baseName =
      subject ||
      selectedMeeting?.name ||
      room?.name ||
      "toplanti-tutanagi";
    exportOfficialReportExcel({
      fileName: `${baseName}-tutanak`,
      subject,
      dateTime: dateLabel,
      topics,
      todos,
      desicions,
      participants,
    });
  };

  const fieldProps = {
    className: classes.textField,
    fullWidth: true,
    InputProps: {
      readOnly,
      disableUnderline: true,
    },
  };

  if (!isOpenOfficialReport) {
    return (
      <div className={classes.root}>
        <div className={classes.minimized}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <IconButton size="small">
              {intl.formatMessage({
                id: "conference.meetingReport",
                defaultMessage: "Meeting Report",
              })}
            </IconButton>
          </div>
          <CallMadeOutlined
            onClick={(e) => {
              e.stopPropagation();
              dispatch(setOfficialReportOpen(true));
            }}
            style={{
              cursor: "pointer",
              height: 16,
              width: 16,
              color: "#2540E4",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Box style={{ padding: isDownloading ? 8 : 16 }}>
        <div className={classes.toolbar}>
          <div className={classes.toolbarActions}>
            {canEdit && isCreator && me?.id?.length > 8 && (
              <>
                <Tooltip title="Excel olarak indir">
                  <IconButton
                    className={classes.toolBtn}
                    onClick={handleDownloadExcel}
                    size="small"
                  >
                    <GuardianDownloadIcon style={{ color: "#2540E4" }} />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={intl.formatMessage({
                    id: "conference.save",
                    defaultMessage: "Save",
                  })}
                >
                  <IconButton
                    className={classes.toolBtn}
                    onClick={handleSaveOfficialReport}
                    size="small"
                  >
                    <SaveIcon style={{ color: "#2540E4" }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {!canEdit && isCreator && me?.id === room?.creator?.id && (
                <Tooltip title="Excel olarak indir">
                  <IconButton
                    className={classes.toolBtn}
                    onClick={handleDownloadExcel}
                    size="small"
                  >
                    <GuardianDownloadIcon style={{ color: "#2540E4" }} />
                  </IconButton>
                </Tooltip>
              )}
          </div>
          {!isDownloading && (
            <div className={classes.toolbarActions}>
              <Tooltip
                title={intl.formatMessage({
                  id: "conference.minize",
                  defaultMessage: "Minimize",
                })}
              >
                <IconButton
                  className={classes.toolBtn}
                  size="small"
                  onClick={(e) => {
                    if (me?.id?.length > 8 && me?.id === room?.creator?.id) {
                      handleSaveOfficialReport();
                    }
                    e.stopPropagation();
                    dispatch(setOfficialReportOpen(false));
                  }}
                >
                  <MinimizeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={intl.formatMessage({
                  id: "conference.close",
                  defaultMessage: "Close",
                })}
              >
                <IconButton
                  className={classes.toolBtn}
                  size="small"
                  onClick={(e) => {
                    if (me?.id?.length > 8 && me?.id === room?.creator?.id) {
                      handleSaveOfficialReport();
                    }
                    e.stopPropagation();
                    dispatch(setOfficialReportOpenGlobal(false));
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>

        <div className={classes.card}>
          <Typography className={classes.mainTitle}>
            <FormattedMessage
              id="conference.meetingReport"
              defaultMessage="Meeting Report"
            />
          </Typography>

          <div className={classes.section}>
            <div className={classes.row}>
              <div className={classes.labelCell}>
                <FormattedMessage
                  id="conference.dateTime"
                  defaultMessage="Date Time"
                />
              </div>
              <div className={classes.fieldCell}>
                <TextField
                  {...fieldProps}
                  value={dateValue}
                  InputProps={{ ...fieldProps.InputProps, readOnly: true }}
                />
              </div>
            </div>
            <div className={classes.row}>
              <div className={classes.labelCell}>
                <FormattedMessage
                  id="conference.subject"
                  defaultMessage="Subject"
                />
              </div>
              <div className={classes.fieldCell}>
                <TextField
                  {...fieldProps}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage
                  id="conference.topics"
                  defaultMessage="Topics"
                />
              </Typography>
              {canEdit && (
                <Button
                  className={classes.addBtn}
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={handleAddTopic}
                >
                  <FormattedMessage
                    id="conference.addNewTopic"
                    defaultMessage="Add New Topic"
                  />
                </Button>
              )}
            </div>
            {topics.map((topic, index) => (
              <div className={classes.row} key={`topic-${index}`}>
                <div className={classes.indexCell}>{index + 1}</div>
                <div className={classes.fieldCell}>
                  <TextField
                    {...fieldProps}
                    multiline
                    value={topic || ""}
                    onChange={(e) => updateTopic(index, e.target.value)}
                  />
                </div>
                {canEdit && (
                  <DeleteRowButton
                    onClick={() => handleRemoveTopic(index)}
                    label="Başlığı sil"
                  />
                )}
              </div>
            ))}
          </div>

          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage id="conference.toDo" defaultMessage="To Do" />
              </Typography>
              {canEdit && (
                <Button
                  className={classes.addBtn}
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={handleAddTodo}
                >
                  <FormattedMessage
                    id="conference.andToDo"
                    defaultMessage="Add New To Do"
                  />
                </Button>
              )}
            </div>
            <div className={classes.row}>
              <div className={classes.indexCell}>#</div>
              <div className={classes.colHead}>
                <FormattedMessage
                  id="conference.description"
                  defaultMessage="Description"
                />
              </div>
              <div className={classes.colHead} style={{ flex: "0 0 22%" }}>
                <FormattedMessage
                  id="conference.owner"
                  defaultMessage="Responsible Side/Person"
                />
              </div>
              <div className={classes.colHead} style={{ flex: "0 0 18%" }}>
                <FormattedMessage
                  id="conference.endDate"
                  defaultMessage="Completion Date"
                />
              </div>
              {canEdit && <div className={classes.deleteCell} />}
            </div>
            {todos.map((todo, index) => (
              <div className={classes.row} key={`todo-${index}`}>
                <div className={classes.indexCell}>{index + 1}</div>
                <div className={classes.fieldCell}>
                  <TextField
                    {...fieldProps}
                    multiline
                    value={todo?.todo || ""}
                    onChange={(e) => updateTodo(index, "todo", e.target.value)}
                  />
                </div>
                <div className={classes.fieldCell} style={{ flex: "0 0 22%" }}>
                  <TextField
                    {...fieldProps}
                    value={todo?.owner || ""}
                    onChange={(e) => updateTodo(index, "owner", e.target.value)}
                  />
                </div>
                <div className={classes.fieldCell} style={{ flex: "0 0 18%" }}>
                  <TextField
                    {...fieldProps}
                    value={todo?.endDate || ""}
                    onChange={(e) =>
                      updateTodo(index, "endDate", e.target.value)
                    }
                  />
                </div>
                {canEdit && (
                  <DeleteRowButton
                    onClick={() => handleRemoveTodo(index)}
                    label="Yapılacağı sil"
                  />
                )}
              </div>
            ))}
          </div>

          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage
                  id="conference.desicions"
                  defaultMessage="Decisions"
                />
              </Typography>
              {canEdit && (
                <Button
                  className={classes.addBtn}
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={handleAddDesicion}
                >
                  <FormattedMessage
                    id="conference.addDesicion"
                    defaultMessage="Add New Desicion"
                  />
                </Button>
              )}
            </div>
            {desicions.map((desicion, index) => (
              <div className={classes.row} key={`decision-${index}`}>
                <div className={classes.indexCell}>{index + 1}</div>
                <div className={classes.fieldCell}>
                  <TextField
                    {...fieldProps}
                    multiline
                    value={desicion || ""}
                    onChange={(e) => updateDecision(index, e.target.value)}
                  />
                </div>
                {canEdit && (
                  <DeleteRowButton
                    onClick={() => handleRemoveDesicion(index)}
                    label="Kararı sil"
                  />
                )}
              </div>
            ))}
          </div>

          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <Typography className={classes.sectionTitle}>
                <FormattedMessage
                  id="conference.participants"
                  defaultMessage="Participants"
                />
              </Typography>
              {canEdit && (
                <Button
                  className={classes.addBtn}
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={handleAddParticipants}
                >
                  <FormattedMessage
                    id="conference.addParticipant"
                    defaultMessage="Add New Participant"
                  />
                </Button>
              )}
            </div>
            <div className={classes.row}>
              <div className={classes.colHead} style={{ flex: "0 0 14%" }}>
                <FormattedMessage id="conference.side" defaultMessage="Side" />
              </div>
              <div className={classes.colHead} style={{ flex: "0 0 16%" }}>
                <FormattedMessage
                  id="conference.title"
                  defaultMessage="Title"
                />
              </div>
              <div className={classes.colHead} style={{ flex: "0 0 22%" }}>
                <FormattedMessage id="conference.name" defaultMessage="Name" />
              </div>
              <div className={classes.colHead} style={{ flex: "0 0 26%" }}>
                <FormattedMessage
                  id="conference.email"
                  defaultMessage="Email"
                />
              </div>
              <div
                className={classes.colHead}
                style={{ flex: canEdit ? "0 0 calc(22% - 40px)" : "0 0 22%" }}
              >
                <FormattedMessage
                  id="conference.phone"
                  defaultMessage="Phone"
                />
              </div>
              {canEdit && <div className={classes.deleteCell} />}
            </div>
            {participants?.length ? (
              participants.map((p, index) => (
                <div className={classes.row} key={`participant-${index}`}>
                  <div className={classes.fieldCell} style={{ flex: "0 0 14%" }}>
                    <TextField
                      {...fieldProps}
                      value={p?.side || ""}
                      onChange={(e) =>
                        updateParticipant(index, "side", e.target.value)
                      }
                    />
                  </div>
                  <div className={classes.fieldCell} style={{ flex: "0 0 16%" }}>
                    <TextField
                      {...fieldProps}
                      value={p?.position || ""}
                      onChange={(e) =>
                        updateParticipant(index, "position", e.target.value)
                      }
                    />
                  </div>
                  <div className={classes.fieldCell} style={{ flex: "0 0 22%" }}>
                    <TextField
                      {...fieldProps}
                      value={p?.displayName || ""}
                      onChange={(e) =>
                        updateParticipant(index, "displayName", e.target.value)
                      }
                    />
                  </div>
                  <div className={classes.fieldCell} style={{ flex: "0 0 26%" }}>
                    <TextField
                      {...fieldProps}
                      value={p?.email || ""}
                      onChange={(e) =>
                        updateParticipant(index, "email", e.target.value)
                      }
                    />
                  </div>
                  <div
                    className={classes.fieldCell}
                    style={{
                      flex: canEdit ? "0 0 calc(22% - 40px)" : "0 0 22%",
                    }}
                  >
                    <TextField
                      {...fieldProps}
                      value={p?.phoneNumber || ""}
                      onChange={(e) =>
                        updateParticipant(index, "phoneNumber", e.target.value)
                      }
                    />
                  </div>
                  {canEdit && (
                    <DeleteRowButton
                      onClick={() => handleRemoveParticipant(index)}
                      label="Katılımcıyı sil"
                    />
                  )}
                </div>
              ))
            ) : (
              <Typography className={classes.emptyHint}>
                Katılımcı bulunamadı
              </Typography>
            )}
          </div>
        </div>
      </Box>
    </div>
  );
};

export default ConferenceOfficialReport;
